import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowLeft, ArrowRight } from 'lucide-react';
import { events as allEvents } from '../data/siteData';
import { 
  legendsEveningGallery, 
  kochaniCharityGallery,
  trifonMonumentGallery,
  golfTournamentGallery,
  currentGolfTournamentGallery,
  macedoniaGallery,
  hopeMatchGallery  
} from '../data/siteData';

const EventDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { eventId } = useParams();
  
  const event = allEvents.find(e => e.id === eventId);

  // Функция за вземане на снимки според събитието
  const getEventPhotos = () => {
    let gallery = null;
    
    if (eventId === 'event_7') {
      gallery = kochaniCharityGallery;
    }
    else if (eventId === 'event_8') {
      gallery = legendsEveningGallery;
    }
    else if (eventId === 'event_6') {
      gallery = trifonMonumentGallery;
    }
    else if (eventId === 'event_3') {
      gallery = golfTournamentGallery;
    }
    else if (eventId === 'event_5') {
      gallery = currentGolfTournamentGallery;
    }
    else if (eventId === 'event_1') {
      gallery = macedoniaGallery;
    }
    else if (eventId === 'event_11' || eventId === 'event_9') {
      gallery = hopeMatchGallery;
    }
    
    if (!gallery || gallery.length === 0) {
      return [];
    }

    if (eventId === 'event_5') {
      return [gallery[0], gallery[6], gallery[66], gallery[119]]
        .filter(Boolean)
        .map(img => img.url);
    }
    
    return gallery.slice(0, 4).map(img => img.url);
  };

  // Функция за получаване на името на галерията за хеш навигация
  const getGalleryHash = () => {
    switch (eventId) {
      case 'event_7':
        return 'kochani-charity';
      case 'event_8':
        return 'legends-evening';
      case 'event_6':
        return 'trifon-monument';
      case 'event_3':
        return 'golf-tournament';
      case 'event_5':
        return 'golf-tournament-2026';
      case 'event_1':
        return 'macedonia-event';
      case 'event_11':
      case 'event_9':
        return 'hope-match';
      default:
        return null;
    }
  };

  // Generate calendar event (Google Calendar URL)
  const addToCalendar = () => {
    if (!event) return;
    
    const parseEventDate = (dateStr) => {
      if (dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
        return new Date(dateStr);
      }
      const months = {
        'Януари': 0, 'Февруари': 1, 'Март': 2, 'Април': 3, 'Май': 4, 'Юни': 5,
        'Юли': 6, 'Август': 7, 'Септември': 8, 'Октомври': 9, 'Ноември': 10, 'Декември': 11,
      };
      const parts = dateStr.split(' ');
      if (parts.length >= 3) {
        const day = parseInt(parts[0]);
        const month = months[parts[1]];
        const year = parseInt(parts[2]);
        if (!isNaN(day) && month !== undefined && !isNaN(year)) {
          return new Date(year, month, day);
        }
      }
      return new Date(dateStr);
    };

    const eventDate = parseEventDate(event.date);
    const title = encodeURIComponent(i18n.language === 'bg' ? event.title_bg : event.title);
    const location = encodeURIComponent(event.location);
    const description = encodeURIComponent(i18n.language === 'bg' ? event.description_bg : event.description);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };
    
    const startDate = formatDate(eventDate);
    const endDate = formatDate(new Date(eventDate.getTime() + 3 * 60 * 60 * 1000));
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${description}&location=${location}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-32">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-foreground mb-4">
            {i18n.language === 'bg' ? 'Събитието не е намерено' : 'Event not found'}
          </h1>
          <Link
            to={`/${i18n.language}/events`}
            className="inline-flex items-center space-x-2 text-foreground hover:text-accent transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{i18n.language === 'bg' ? 'Обратно към събития' : 'Back to events'}</span>
          </Link>
        </div>
      </div>
    );
  }

  const eventPhotos = getEventPhotos();
  const galleryHash = getGalleryHash();
  
  // URL с хеш за директно отваряне на конкретната галерия
  const galleryUrl = galleryHash 
    ? `/${i18n.language}/gallery#${galleryHash}`
    : `/${i18n.language}/gallery`;

  return (
    <div className="min-h-screen bg-background pt-16 md:pt-20">
      {/* Hero Image Section */}
      <div className="relative h-[50vh] md:h-[60vh] lg:h-[70vh] w-full overflow-hidden">
        <img
          src={event.detail_image || event.image}
          alt={i18n.language === 'bg' ? event.title_bg : event.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        
        {/* Back Button */}
        <Link
          to={`/${i18n.language}/events`}
          className="absolute top-24 md:top-32 left-6 md:left-12 inline-flex items-center space-x-2 text-white/80 hover:text-white transition-colors bg-black/30 backdrop-blur-sm px-4 py-2 rounded-md z-30"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{i18n.language === 'bg' ? 'Обратно' : 'Back'}</span>
        </Link>

        {/* Category Badge */}
        <div className="absolute top-24 md:top-32 right-6 md:right-12 bg-accent text-accent-foreground px-4 py-2 font-bold uppercase text-sm rounded-md z-30">
          {event.category === 'match' ? (i18n.language === 'bg' ? 'Мач' : 'Match') : 
           event.category === 'charity' ? (i18n.language === 'bg' ? 'Благотворителност' : 'Charity') : 
           event.category === 'gala' ? (i18n.language === 'bg' ? 'Гала' : 'Gala') :
           (i18n.language === 'bg' ? 'Събитие' : 'Event')}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-20 z-20">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-4xl"
          >
            {i18n.language === 'bg' ? event.title_bg : event.title}
          </motion.h1>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-12 md:py-16">
        {/* Event Info Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-wrap gap-6 md:gap-10 mb-10 md:mb-14 pb-8 border-b border-border"
        >
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-accent/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{i18n.language === 'bg' ? 'Дата' : 'Date'}</p>
              <p className="text-lg font-medium text-foreground">{event.date}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-accent/10 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{i18n.language === 'bg' ? 'Локация' : 'Location'}</p>
              <p className="text-lg font-medium text-foreground">{event.location}</p>
            </div>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="lg:col-span-2"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-6">
              {i18n.language === 'bg' ? 'За събитието' : 'About the Event'}
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <div className="text-lg md:text-xl text-muted-foreground leading-relaxed whitespace-pre-line">
                {(i18n.language === 'bg' ? event.description_bg : event.description).split('\n').map((paragraph, idx) => {
                  const trimmedParagraph = paragraph.trim();
                  
                  if (!trimmedParagraph) return null;
                  
                  if (trimmedParagraph.startsWith('**') && trimmedParagraph.endsWith('**')) {
                    const cleanTitle = trimmedParagraph.slice(2, -2);
                    return (
                      <h3 key={idx} className="text-white/70 font-semibold text-xl md:text-2xl mt-6 mb-3">
                        {cleanTitle}
                      </h3>
                    );
                  }
                  
                  return <p key={idx} className="mb-4">{trimmedParagraph}</p>;
                })}
              </div>
            </div>

            {event.video_url && (
              <div className="mt-10">
                <h3 className="font-heading text-xl font-bold text-foreground mb-4">
                  {i18n.language === 'bg' ? 'Видео' : 'Video'}
                </h3>
                <div className="relative aspect-video overflow-hidden border border-border bg-black">
                  <video
                    src={event.video_url}
                    controls
                    className="w-full h-full object-contain"
                    poster={event.image}
                  />
                </div>
              </div>
            )}
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="lg:col-span-1"
          >
            <div className="sticky top-32 space-y-4">
              {event.ticket_url && event.show_ticket_button !== false && (
                <button
                  onClick={() => window.open(event.ticket_url, '_blank')}
                  className="w-full bg-accent text-accent-foreground font-bold uppercase tracking-wider px-8 py-4 hover:bg-primary hover:text-primary-foreground transition-all"
                  data-testid="buy-ticket-btn"
                >
                  {t('events.buyTickets')}
                </button>
              )}
              
              {event.show_calendar_button !== false && (
                <button
                  onClick={addToCalendar}
                  className="w-full border-2 border-border text-foreground font-medium px-8 py-4 hover:bg-secondary transition-all flex items-center justify-center space-x-2"
                  data-testid="add-to-calendar-btn"
                >
                  <Calendar className="w-5 h-5" />
                  <span>{i18n.language === 'bg' ? 'Добави в календар' : 'Add to Calendar'}</span>
                </button>
              )}

              <div className="pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground mb-3">
                  {i18n.language === 'bg' ? 'Сподели събитието' : 'Share this event'}
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`, '_blank')}
                    className="w-10 h-10 bg-muted hover:bg-accent hover:text-white flex items-center justify-center transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </button>
                  <button
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${window.location.href}&text=${encodeURIComponent(i18n.language === 'bg' ? event.title_bg : event.title)}`, '_blank')}
                    className="w-10 h-10 bg-muted hover:bg-accent hover:text-white flex items-center justify-center transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Photo Gallery */}
        {eventPhotos.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-16 md:mt-24"
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                {i18n.language === 'bg' ? 'Снимки от събитието' : 'Event Photos'}
              </h2>
              <Link
                to={galleryUrl}
                className="inline-flex items-center space-x-2 text-foreground hover:text-accent transition-colors group"
              >
                <span>{i18n.language === 'bg' ? 'Виж всички' : 'View all'}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {eventPhotos.map((photo, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="relative aspect-square overflow-hidden border border-border group cursor-pointer"
                >
                  <img
                    src={photo}
                    alt={`Event photo ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EventDetailPage;